import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { transform } from 'lodash';
import React, { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { AppConfigArea, AppConfigKey, QueryKey } from 'enums';
import { useAppConfigValue, useMutation } from 'hooks';
import { formatUtils, validationUtils } from 'utils';

import { DateLabel } from '../DateLabel';
import { CloseDialogResult, Dialog, DialogProps } from '../Dialog';
import { FormControls } from '../FormControls';
import { FormikSelect, FormikSelectOption } from '../FormikSelect';
import { FormikTextField } from '../FormikTextField';
import { StylizedMoney } from '../StylizedMoney';

type FormValues = {
  category: string;
  comment: string;
};

type BaseData = {
  id: string;
  createdAt: string;
  amount: number;
  category?: string;
  comment?: string;
};

type Props<T extends BaseData> = DialogProps<T> & {
  onUpdate: (id: string, values: FormValues) => Promise<any>;
  queryKey: QueryKey;
  dialogTitle: string;
  categoryLabel: string;
  commentLabel: string;
  getAssetCurrencyId: (data: T) => string | undefined;
  renderStatusLabel: (data: T) => React.ReactNode;
  renderUserLabel?: (data: T) => React.ReactNode;
};

export const EditCategoryCommentDialog = <T extends BaseData>({
  open,
  data,
  onClose,
  onUpdate,
  queryKey,
  dialogTitle,
  categoryLabel,
  commentLabel,
  getAssetCurrencyId,
  renderStatusLabel,
  renderUserLabel,
}: Props<T>) => {
  const queryClient = useQueryClient();

  const categoriesConfigQueryResult = useAppConfigValue(
    {
      area: AppConfigArea.Platform,
      key: AppConfigKey.InternalTransferCategories,
    },
    { enabled: open },
  );

  const categoryOptions = useMemo(
    () =>
      transform(
        categoriesConfigQueryResult?.data || {},
        (acc, label, value) => acc.push({ label: label as string, value }),
        [] as FormikSelectOption[],
      ),
    [categoriesConfigQueryResult?.data],
  );

  const { mutate: update } = useMutation((values: FormValues) =>
    onUpdate(data!.id, values),
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        category: Yup.string().notRequired(),
        comment: Yup.string().notRequired(),
      }),
    [],
  );

  const handleSubmit = (
    values: FormValues,
    helpers: FormikHelpers<FormValues>,
  ) => {
    update(values, {
      onSettled: () => helpers.setSubmitting(false),
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        helpers.resetForm();
        onClose({ ok: true });
      },
      onError: (error: AxiosError) => {
        helpers.setErrors(validationUtils.getFormErrors(error));
      },
    });
  };

  return (
    <Formik
      initialValues={{
        category: data?.category || '',
        comment: data?.comment || '',
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form>
          <Dialog
            open={open}
            title={dialogTitle}
            onClose={(result: CloseDialogResult) => {
              if (result.ok) {
                formik.submitForm();
              } else {
                formik.resetForm();
                onClose(result);
              }
            }}
            okDisabled={formik.isSubmitting}
          >
            <FormControls>
              {data && (
                <div className="tw-flex tw-gap-4 tw-mb-4 tw-text-sm">
                  <DateLabel>
                    {formatUtils.formatDate(data.createdAt)}
                  </DateLabel>
                  <StylizedMoney
                    variant="body2"
                    value={data.amount}
                    symbol
                    assetCurrencyId={getAssetCurrencyId(data)}
                  />
                  {renderStatusLabel(data)}
                  {renderUserLabel?.(data)}
                </div>
              )}
              <FormikSelect
                options={categoryOptions}
                label={categoryLabel}
                className="tw-w-full"
                name="category"
                disablePortal={false}
              />
              <FormikTextField
                label={commentLabel}
                name="comment"
                className="tw-w-full"
                multiline
                rows={3}
              />
            </FormControls>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};
